import React from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import {
  Heart,
  Building2,
  GraduationCap,
  Sparkles,
  Sprout,
  ArrowRight,
  Users,
  MapPin,
} from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import { colors, images } from "../config/site";

const PILLARS = [
  {
    icon: Building2,
    title: "Community-Centered Development",
    body:
      "We invest in Philadelphia neighborhoods by rehabilitating and developing quality affordable homes for working families.",
  },
  {
    icon: GraduationCap,
    title: "Housing Stability Education",
    body:
      "Through practical workshops and coaching, we help tenants build the habits and knowledge they need to stay housed long-term.",
  },
  {
    icon: Sprout,
    title: "Long-Term Housing Vision",
    body:
      "Every program and project is designed to support pathways from temporary or unstable housing toward future homeownership.",
  },
  {
    icon: Heart,
    title: "Education & Empowerment",
    body:
      "We believe lasting change happens when families have both safe housing and the tools to manage and protect it.",
  },
];

const AboutPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.cream }}>
      <Header />

      <main className="pt-24 pb-20">
        {/* Hero / Intro */}
        <section className="px-4 pb-16">
          <div className="container mx-auto max-w-6xl">
            <p
              className="text-xs uppercase tracking-widest font-semibold mb-3"
              style={{ color: colors.terracotta }}
            >
              About Dorsey Taylor Homes
            </p>
            <h1
              className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6"
              style={{ color: colors.charcoal }}
            >
              A woman-led, community-centered affordable housing company.
            </h1>
            <p className="text-lg md:text-xl max-w-3xl" style={{ color: colors.textMuted }}>
              We combine affordable housing development with practical housing education —
              so the homes we build become homes households can keep.
            </p>
          </div>
        </section>

        {/* Mission + Image */}
        <section className="px-4 pb-20">
          <div className="container mx-auto max-w-6xl">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div
                className="rounded-3xl p-8 lg:p-10 shadow-sm"
                style={{ backgroundColor: colors.white, borderLeft: `4px solid ${colors.sage}` }}
              >
                <Sparkles className="w-7 h-7 mb-4" style={{ color: colors.gold }} />
                <h2 className="text-2xl lg:text-3xl font-bold mb-4" style={{ color: colors.charcoal }}>
                  Our Mission
                </h2>
                <p style={{ color: colors.textMuted }}>
                  Build lasting housing stability for families and individuals — through <strong>education first</strong>, plus affordable housing development and neighborhood revitalization.
                </p>
              </div>
              <div className="rounded-3xl overflow-hidden shadow-lg">
                <img
                  src={images.projectSurvey}
                  alt="Anissa surveying a property at the start of a rehabilitation project"
                  className="w-full h-[420px] object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* What we believe / pillars */}
        <section className="px-4 pb-20" style={{ backgroundColor: colors.ivory }}>
          <div className="container mx-auto max-w-6xl py-16">
            <div className="max-w-2xl mb-12">
              <p
                className="text-xs uppercase tracking-widest font-semibold mb-3"
                style={{ color: colors.terracotta }}
              >
                What we believe
              </p>
              <h2 className="text-3xl lg:text-4xl font-bold" style={{ color: colors.charcoal }}>
                Housing, education, and community working together.
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              {PILLARS.map(({ icon: Icon, title, body }) => (
                <div
                  key={title}
                  className="rounded-2xl p-7 shadow-sm hover:shadow-md transition-all"
                  style={{ backgroundColor: colors.white }}
                  data-testid={`about-pillar-${title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: "#EFE6D6", color: colors.terracottaDark }}
                  >
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-bold mb-2" style={{ color: colors.charcoal }}>
                    {title}
                  </h3>
                  <p className="text-sm" style={{ color: colors.textMuted }}>
                    {body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Women-led leadership / community */}
        <section className="px-4 pb-20">
          <div className="container mx-auto max-w-6xl">
            <div
              className="rounded-3xl p-8 lg:p-12 shadow-md grid md:grid-cols-3 gap-8 items-center"
              style={{ backgroundColor: colors.primary, color: colors.white }}
            >
              <div className="flex items-center gap-4">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center"
                  style={{ backgroundColor: "rgba(255,255,255,0.12)" }}
                >
                  <Users className="w-7 h-7" style={{ color: colors.gold }} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider" style={{ color: colors.gold }}>
                    Leadership
                  </p>
                  <p className="font-bold">Women-Led</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center"
                  style={{ backgroundColor: "rgba(255,255,255,0.12)" }}
                >
                  <MapPin className="w-7 h-7" style={{ color: colors.gold }} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider" style={{ color: colors.gold }}>
                    Focus
                  </p>
                  <p className="font-bold">Philadelphia Communities</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center"
                  style={{ backgroundColor: "rgba(255,255,255,0.12)" }}
                >
                  <Heart className="w-7 h-7" style={{ color: colors.gold }} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider" style={{ color: colors.gold }}>
                    Centered on
                  </p>
                  <p className="font-bold">Underserved Families</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="px-4">
          <div className="container mx-auto max-w-5xl text-center">
            <h2 className="text-2xl lg:text-3xl font-bold mb-4" style={{ color: colors.charcoal }}>
              Want to learn more or work with us?
            </h2>
            <p className="mb-6" style={{ color: colors.textMuted }}>
              Whether you are a family looking for housing support, an organization interested in
              partnership, or a community member wanting to connect — we would love to hear from you.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/contact" data-testid="about-contact-cta">
                <Button
                  className="text-white rounded-full px-7 py-6 font-semibold"
                  style={{ backgroundColor: colors.primary }}
                >
                  Contact Our Team
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/partnerships" data-testid="about-partnerships-cta">
                <Button
                  variant="outline"
                  className="rounded-full px-7 py-6 font-semibold border-2"
                  style={{ borderColor: colors.terracotta, color: colors.terracotta, backgroundColor: "transparent" }}
                >
                  Partnership Opportunities
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
